/**
 * XmlParser
 * Converts between legacy XML and JSON configuration formats
 */

export class XmlParser {
  /**
   * Convert legacy XML string to JSON configuration
   * @param {string} xmlString - Legacy XML configuration string
   * @returns {Object} JSON configuration object
   */
  static fromLegacyXml(xmlString) {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
    
    // Check for parsing errors
    const parseError = xmlDoc.querySelector('parsererror');
    if (parseError) {
      throw new Error('Invalid XML: ' + parseError.textContent);
    }

    const config = {
      settings: this.parseGeneral(xmlDoc),
      signers: this.parseSigners(xmlDoc),
      integrationData: this.parseIntegrationData(xmlDoc)
    };

    config.settings.enableBridge = true;
    config.settings.enforceBridge = true;
    config.settings.requireAllSignerOccurrences = true;

    return config;
  }

  /**
   * Parse General section to settings
   */
  static parseGeneral(xmlDoc) {
    const general = xmlDoc.querySelector('General');
    if (!general) return {};

    const settings = {};

    // Language
    const language = this.getTextContent(general, 'Language');
    if (language) settings.language = language;

    // Button visibility
    const showCancelButton = this.getTextContent(general, 'showCancelButton');
    if (showCancelButton !== null) settings.showCancelButton = showCancelButton === '1';

    const showViewPDFButton = this.getTextContent(general, 'showViewPDFButton');
    if (showViewPDFButton !== null) settings.showViewPDFButton = showViewPDFButton === '1';

    const showSignPaperButton = this.getTextContent(general, 'showSignPaperButton');
    if (showSignPaperButton !== null) settings.showSignPaperButton = showSignPaperButton === '1';

    const showFinishButton = this.getTextContent(general, 'showFinishButton');
    if (showFinishButton !== null) settings.showFinishButton = showFinishButton === '1';

    const showFinishAndSignPaperButton = this.getTextContent(general, 'showFinishAndSignPaperButton');
    if (showFinishAndSignPaperButton !== null) settings.showFinishAndSignPaperButton = showFinishAndSignPaperButton === '1';

    // Print behaviour
    const showWarningBeforePrint = this.getTextContent(general, 'showWarningBeforePrint');
    if (showWarningBeforePrint !== null) settings.showWarningBeforePrint = showWarningBeforePrint === '1';

    const doNotPrint = this.getTextContent(general, 'doNotPrint');
    if (doNotPrint !== null) settings.doNotPrint = doNotPrint === '1';

    // Custom texts: <labels><label key="actionBar.print">Firmar en papel</label></labels>
    const labels = this.parseLabels(general);
    if (labels) settings.labels = labels;

    // Encryption certificate key
    const encCertKey = this.getTextContent(general, 'enc-cert-key');
    if (encCertKey) settings.encCertKey = encCertKey;

    return settings;
  }

  /**
   * Parse <labels> (custom texts) from General
   * @returns {Object|null} Map of translation key -> text
   */
  static parseLabels(general) {
    const labelNodes = general.querySelectorAll('labels > label');
    const labels = {};

    labelNodes.forEach(node => {
      const key = node.getAttribute('key');
      const text = node.textContent.trim();
      if (key && text) labels[key] = text;
    });

    return Object.keys(labels).length > 0 ? labels : null;
  }

  /**
   * Parse Firmantes section to signers array
   */
  static parseSigners(xmlDoc) {
    const firmantes = xmlDoc.querySelectorAll('Firmantes > Firmante');
    const signers = [];

    firmantes.forEach((firmante, index) => {
      const signer = {};

      // Basic info
      const signerId = this.getTextContent(firmante, 'idSignatory');
      signer.signerId = signerId || `signer-${index + 1}`;

      const signerIdLong = this.getTextContent(firmante, 'idSignatoryLong');
      if (signerIdLong) signer.signerIdLong = signerIdLong;
      
      const label = this.getTextContent(firmante, 'item');
      if (label) signer.label = label;

      // Signed status
      const signed = this.getTextContent(firmante, 'signed');
      signer.isSigned = signed === '1';

      // Timestamp
      const timeStamp = this.getTextContent(firmante, 'timeStamp');
      if (timeStamp) signer.timeStamp = timeStamp;

      // Mandatory
      const mandatory = this.getTextContent(firmante, 'mandatoryToPostpone');
      signer.isMandatory = mandatory === '1';

      // Signature type (jsigMode: 1 = certificate, otherwise dtu)
      const jsigMode = this.getTextContent(firmante, 'jsigMode');
      signer.signatureType = jsigMode === '1' ? 'certificate' : 'dtu';

      // Images
      const img1B64 = this.getTextContent(firmante, 'img1B64');
      if (img1B64) signer.img1B64 = img1B64;

      const img2B64 = this.getTextContent(firmante, 'img2B64');
      if (img2B64) signer.img2B64 = img2B64;

      // Widget configuration
      signer.widget = this.parseWidget(firmante);

      // EBP text
      signer.ebpText = this.parseEbpTextos(firmante);

      // TSP configuration
      const tspActivate = this.getTextContent(firmante, 'tsp-activate');
      if (tspActivate !== null) {
        signer.tspActivate = tspActivate === '1';
        signer.tspUrl = this.getTextContent(firmante, 'tsp-url') || '';
        signer.tspUser = this.getTextContent(firmante, 'tsp-user') || '';
        signer.tspPassword = this.getTextContent(firmante, 'tsp-password') || '';
      }

      // OCSP configuration
      const ocspActivate = this.getTextContent(firmante, 'ocsp-activate');
      if (ocspActivate !== null) {
        signer.ocspActivate = ocspActivate === '1';
        signer.ocspUrl = this.getTextContent(firmante, 'ocsp-url') || '';
        signer.ocspUser = this.getTextContent(firmante, 'ocsp-user') || '';
        signer.ocspPassword = this.getTextContent(firmante, 'ocsp-password') || '';
      }

      signers.push(signer);
    });

    return signers;
  }

  /**
   * Parse widget configuration from Firmante
   */
  static parseWidget(firmante) {
    const widget = {};
    const general = firmante.ownerDocument.querySelector('General');

    // Check for field positioning (PDFEmptySig) - from Firmante first, fallback to General
    const pdfEmptySigEnable = this.getTextContent(firmante, 'PDFEmptySig_enable')
      || (general ? this.getTextContent(general, 'PDFEmptySig_enable') : null);
    if (pdfEmptySigEnable === '1') {
      widget.type = 'field';
      widget.fieldName = this.getTextContent(firmante, 'PDFEmptySig_fieldname')
        || (general ? this.getTextContent(general, 'PDFEmptySig_fieldname') : null)
        || '';
    }
    // Check for float positioning (autoPos)
    else {
      const autoPosActivate = this.getTextContent(firmante, 'widget-autoPos-activate');
      if (autoPosActivate === '1') {
        widget.type = 'float';
        widget.text = this.getTextContent(firmante, 'widget-autoPos-text') || '';
        
        const gapX = this.getTextContent(firmante, 'widget-autoPos-desfaseX');
        if (gapX !== null) widget.gapX = parseInt(gapX, 10);
        
        const gapY = this.getTextContent(firmante, 'widget-autoPos-desfaseY');
        if (gapY !== null) widget.gapY = parseInt(gapY, 10);
      } else {
        // Fixed positioning
        widget.type = 'fixed';
        
        const page = this.getTextContent(firmante, 'widget-Page');
        if (page !== null) widget.page = parseInt(page, 10);
        
        const x = this.getTextContent(firmante, 'widget-X');
        if (x !== null) widget.x = parseInt(x, 10);
        
        const y = this.getTextContent(firmante, 'widget-Y');
        if (y !== null) widget.y = parseInt(y, 10);
      }
    }

    // Widget dimensions from Firmante first, fallback to General
    const width = this.getTextContent(firmante, 'widget-Ancho');
    if (width !== null) {
      widget.width = parseInt(width, 10);
    } else if (general) {
      const gWidth = this.getTextContent(general, 'widget-Ancho');
      if (gWidth !== null) widget.width = parseInt(gWidth, 10);
    }

    const height = this.getTextContent(firmante, 'widget-Alto');
    if (height !== null) {
      widget.height = parseInt(height, 10);
    } else if (general) {
      const gHeight = this.getTextContent(general, 'widget-Alto');
      if (gHeight !== null) widget.height = parseInt(gHeight, 10);
    }

    // Custom text (base64)
    const customTextB64 = this.getTextContent(firmante, 'widget-Textcustom');
    if (customTextB64) widget.customTextB64 = customTextB64;

    return widget;
  }

  /**
   * Parse ebp-textos to ebpText object
   */
  static parseEbpTextos(firmante) {
    const ebpTextos = firmante.querySelector('ebp-textos');
    if (!ebpTextos) return null;

    const ebpText = {};
    const children = ebpTextos.children;
    
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      ebpText[child.tagName] = child.textContent.trim();
    }

    return Object.keys(ebpText).length > 0 ? ebpText : null;
  }

  /**
   * Parse integrationData section
   */
  static parseIntegrationData(xmlDoc) {
    const integrationData = xmlDoc.querySelector('integrationData');
    if (!integrationData) return {};

    const data = {};
    const children = integrationData.children;
    
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      const value = child.textContent.trim();
      
      // Try to parse as number or boolean
      if (value === 'true') {
        data[child.tagName] = true;
      } else if (value === 'false') {
        data[child.tagName] = false;
      } else if (!isNaN(value) && value !== '') {
        data[child.tagName] = Number(value);
      } else {
        data[child.tagName] = value;
      }
    }

    return data;
  }

  /**
   * Helper to get text content of an element
   */
  static getTextContent(parent, tagName) {
    const element = parent.querySelector(tagName);
    if (!element) return null;
    const text = element.textContent.trim();
    return text || null;
  }

  /**
   * Convert JSON configuration to legacy XML string
   * @param {Object} config - JSON configuration object
   * @returns {string} Legacy XML configuration string
   */
  static toLegacyXml(config) {
    let xml = '<?xml version="1.0" encoding="UTF-8" standalone="no"?>\n<Configuracion>\n\n';
    
    // Generate General section
    xml += this.generateGeneral(config.settings || {});
    
    // Generate Firmantes section
    xml += this.generateFirmantes(config.signers || []);
    
    // Generate integrationData section if present
    if (config.integrationData && Object.keys(config.integrationData).length > 0) {
      xml += this.generateIntegrationData(config.integrationData);
    }
    
    xml += '</Configuracion>';
    return xml;
  }

  /**
   * Generate General XML section from settings
   */
  static generateGeneral(settings) {
    let xml = '\t<General>\n\n';
    
    if (settings.language) {
      xml += `\t\t<Language>${this.escapeXml(settings.language)}</Language>\n`;
    }
    
    if (settings.showCancelButton !== undefined) {
      xml += `\t\t<showCancelButton>${settings.showCancelButton ? '1' : '0'}</showCancelButton>\n`;
    }
    if (settings.showViewPDFButton !== undefined) {
      xml += `\t\t<showViewPDFButton>${settings.showViewPDFButton ? '1' : '0'}</showViewPDFButton>\n`;
    }
    if (settings.showSignPaperButton !== undefined) {
      xml += `\t\t<showSignPaperButton>${settings.showSignPaperButton ? '1' : '0'}</showSignPaperButton>\n`;
    }
    if (settings.showFinishButton !== undefined) {
      xml += `\t\t<showFinishButton>${settings.showFinishButton ? '1' : '0'}</showFinishButton>\n`;
    }
    if (settings.showFinishAndSignPaperButton !== undefined) {
      xml += `\t\t<showFinishAndSignPaperButton>${settings.showFinishAndSignPaperButton ? '1' : '0'}</showFinishAndSignPaperButton>\n`;
    }
    if (settings.showWarningBeforePrint !== undefined) {
      xml += `\t\t<showWarningBeforePrint>${settings.showWarningBeforePrint ? '1' : '0'}</showWarningBeforePrint>\n`;
    }
    if (settings.doNotPrint !== undefined) {
      xml += `\t\t<doNotPrint>${settings.doNotPrint ? '1' : '0'}</doNotPrint>\n`;
    }
    if (settings.labels && Object.keys(settings.labels).length > 0) {
      xml += '\t\t<labels>\n';
      for (const [key, value] of Object.entries(settings.labels)) {
        xml += `\t\t\t<label key="${this.escapeXml(key)}">${this.escapeXml(value)}</label>\n`;
      }
      xml += '\t\t</labels>\n';
    }
    
    if (settings.encCertKey) {
      xml += `\t\t<enc-cert-key>${this.escapeXml(settings.encCertKey)}</enc-cert-key>\n`;
    }
    
    xml += '\n\t</General>\n\n';
    return xml;
  }

  /**
   * Generate Firmantes XML section from signers array
   */
  static generateFirmantes(signers) {
    let xml = '\t<Firmantes>\n\n';
    
    signers.forEach(signer => {
      xml += '\t\t<Firmante>\n\n';
      
      // Basic info
      if (signer.label) {
        xml += `\t\t\t<item>${this.escapeXml(signer.label)}</item>\n`;
      }
      
      // Images
      if (signer.img1B64) {
        xml += `\t\t\t<img1B64>${signer.img1B64}</img1B64>\n`;
      }
      if (signer.img2B64) {
        xml += `\t\t\t<img2B64>${signer.img2B64}</img2B64>\n`;
      }
      
      // Signed status
      xml += `\t\t\t<signed>${signer.isSigned ? '1' : '0'}</signed>\n`;
      xml += `\t\t\t<timeStamp>${signer.timeStamp || ''}</timeStamp>\n`;
      
      if (signer.signerId) {
        xml += `\t\t\t<idSignatory>${this.escapeXml(signer.signerId)}</idSignatory>\n`;
      }
      if (signer.signerIdLong) {
        xml += `\t\t\t<idSignatoryLong>${this.escapeXml(signer.signerIdLong)}</idSignatoryLong>\n`;
      }
      
      // Mandatory
      xml += `\t\t\t<mandatoryToPostpone>${signer.isMandatory ? '1' : '0'}</mandatoryToPostpone>\n`;
      
      // Signature type
      xml += `\t\t\t<jsigMode>${signer.signatureType === 'certificate' ? '1' : '0'}</jsigMode>\n`;
      
      // Widget configuration
      if (signer.widget) {
        xml += this.generateWidget(signer.widget);
      }
      
      // EBP text
      if (signer.ebpText) {
        xml += this.generateEbpTextos(signer.ebpText);
      }
      
      // TSP configuration
      if (signer.tspActivate !== undefined) {
        xml += `\t\t\t<tsp-activate>${signer.tspActivate ? '1' : '0'}</tsp-activate>\n`;
        if (signer.tspUrl) xml += `\t\t\t<tsp-url>${this.escapeXml(signer.tspUrl)}</tsp-url>\n`;
        if (signer.tspUser) xml += `\t\t\t<tsp-user>${this.escapeXml(signer.tspUser)}</tsp-user>\n`;
        if (signer.tspPassword) xml += `\t\t\t<tsp-password>${this.escapeXml(signer.tspPassword)}</tsp-password>\n`;
      }
      
      // OCSP configuration
      if (signer.ocspActivate !== undefined) {
        xml += `\t\t\t<ocsp-activate>${signer.ocspActivate ? '1' : '0'}</ocsp-activate>\n`;
        if (signer.ocspUrl) xml += `\t\t\t<ocsp-url>${this.escapeXml(signer.ocspUrl)}</ocsp-url>\n`;
        if (signer.ocspUser) xml += `\t\t\t<ocsp-user>${this.escapeXml(signer.ocspUser)}</ocsp-user>\n`;
        if (signer.ocspPassword) xml += `\t\t\t<ocsp-password>${this.escapeXml(signer.ocspPassword)}</ocsp-password>\n`;
      }
      
      xml += '\n\t\t</Firmante>\n\n';
    });
    
    xml += '\t</Firmantes>\n\n';
    return xml;
  }

  /**
   * Generate widget XML from widget object
   */
  static generateWidget(widget) {
    let xml = '';
    
    if (widget.type === 'field') {
      xml += `\t\t\t<PDFEmptySig_enable>1</PDFEmptySig_enable>\n`;
      xml += `\t\t\t<PDFEmptySig_fieldname>${this.escapeXml(widget.fieldName || '')}</PDFEmptySig_fieldname>\n`;
    } else if (widget.type === 'float') {
      xml += `\t\t\t<widget-autoPos-activate>1</widget-autoPos-activate>\n`;
      if (widget.text) xml += `\t\t\t<widget-autoPos-text>${this.escapeXml(widget.text)}</widget-autoPos-text>\n`;
      if (widget.gapX !== undefined) xml += `\t\t\t<widget-autoPos-desfaseX>${widget.gapX}</widget-autoPos-desfaseX>\n`;
      if (widget.gapY !== undefined) xml += `\t\t\t<widget-autoPos-desfaseY>${widget.gapY}</widget-autoPos-desfaseY>\n`;
    } else {
      // Fixed positioning
      if (widget.page !== undefined) xml += `\t\t\t<widget-Page>${widget.page}</widget-Page>\n`;
      if (widget.x !== undefined) xml += `\t\t\t<widget-X>${widget.x}</widget-X>\n`;
      if (widget.y !== undefined) xml += `\t\t\t<widget-Y>${widget.y}</widget-Y>\n`;
    }

    // Dimensions
    if (widget.width !== undefined) xml += `\t\t\t<widget-Ancho>${widget.width}</widget-Ancho>\n`;
    if (widget.height !== undefined) xml += `\t\t\t<widget-Alto>${widget.height}</widget-Alto>\n`;

    // Custom text (base64)
    if (widget.customTextB64) xml += `\t\t\t<widget-Textcustom>${widget.customTextB64}</widget-Textcustom>\n`;
    
    return xml;
  }

  /**
   * Generate ebp-textos XML from ebpText object
   */
  static generateEbpTextos(ebpText) {
    if (!ebpText || Object.keys(ebpText).length === 0) return '';
    
    let xml = '\t\t\t<ebp-textos>\n';
    
    for (const [key, value] of Object.entries(ebpText)) {
      xml += `\t\t\t\t<${key}>${this.escapeXml(value)}</${key}>\n`;
    }
    
    xml += '\t\t\t</ebp-textos>\n';
    return xml;
  }

  /**
   * Generate integrationData XML section
   */
  static generateIntegrationData(integrationData) {
    let xml = '\t<integrationData>\n';
    
    for (const [key, value] of Object.entries(integrationData)) {
      xml += `\t\t<${key}>${this.escapeXml(String(value))}</${key}>\n`;
    }
    
    xml += '\t</integrationData>\n\n';
    return xml;
  }

  /**
   * Escape special XML characters
   */
  static escapeXml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }
}
